import { id } from 'date-fns/locale';
import { ApolloClient } from '@apollo/client';
import gql from 'graphql-tag';

export class FormInput {
  id: number;
  code: string;
  seriesId: number;
  codePrefix: string;
  codeNumber: number;
  notes: string;
  transactionDate: string;
  wasteOriginTypeId: number;
  fromCollectionpointId: number;
  fromWastereceptionpointId: number;
  toWastereceptionpointId: number;
  vehicleId: number;
  driverId: number;
  isActive: boolean;
  collectorId: number;
  internalTransactionLines: {
    id: number;
    aa: number;
    materialId: number;
    containersQty: number;
    netWeight: number;
    material: {
      id: number;
      name: string;
    };
  }[];

  constructor() {
    this.id = undefined;
    this.code = undefined;
    this.seriesId = undefined;
    this.codePrefix = undefined;
    this.codeNumber = undefined;
    this.notes = undefined;
    this.transactionDate = undefined;
    this.wasteOriginTypeId = undefined;
    this.fromCollectionpointId = undefined;
    this.fromWastereceptionpointId = undefined;
    this.toWastereceptionpointId = undefined;
    this.vehicleId = undefined;
    this.driverId = undefined;
    this.isActive = undefined;
    this.collectorId = undefined;
    this.internalTransactionLines = undefined;
  }
}

export const gqlGetQuery = gql`
  query getInternalTransactions($limit: Int, $offset: Int, $order: String, $where: String) {
    getInternalTransactions(limit: $limit, offset: $offset, order: $order, where: $where) {
      data {
        id
        code
        seriesId
        codePrefix
        codeNumber
        notes
        transactionDate

        wasteOriginTypeId
        fromWastereceptionpointId
        fromCollectionpointId
        toWastereceptionpointId
        isActive
        fromCollectionpoint {
          id
          name
          addressLine1
          addressArea
          addressCity
        }
        fromWastereceptionpoint {
          id
          code
          name
        }
        toWastereceptionpoint {
          id
          code
          name
        }
        internalTransactionLines {
          id
          aa
          materialId
          containersQty
          netWeight
          material {
            id
            name
          }
        }
      }
      recordCount
    }
    getInternalTransactionLinesSums(where: $where) {
      materialId
      totalContainersQty
      totalNetWeight
      materialName
    }
  }
`;

export const gqlGetByIdQuery = gql`
  query getInternalTransactionById($id: Int) {
    getInternalTransactionById(id: $id) {
      id
      code
      seriesId
      codePrefix
      codeNumber
      notes
      transactionDate
      wasteOriginTypeId
      fromCollectionpointId
      fromWastereceptionpointId
      toWastereceptionpointId
      vehicleId
      driverId
      isActive
      collectorId
      collector {
        id
        name
      }
      fromCollectionpoint {
        id
        name
        addressLine1
        addressArea
        addressCity
      }
      fromWastereceptionpoint {
        id
        code
        name
      }
      toWastereceptionpoint {
        id
        code
        name
      }
      internalTransactionLines {
        id
        aa
        materialId
        containersQty
        netWeight
        material {
          id
          name
        }
        idocs {
          id
          idocPrefix
          identificationDocument
          dateCollected
          collectionPointName
          colpointAddressLine1
          collectionPointId
          collectorCompanyName
          wastereceptionpointCompanyName
          dateStorage
          vehPlateNumber
          driverFullName
          netWeight
          receptionNetWeight
          finalDestinationDate
          finalDestinationNetWeight
          finaldestinationCompanyName
          finaldestinationCompanyNoteDocNumber
        }
      }
      idocs {
        id
        idocPrefix
        identificationDocument
        dateCollected
        collectionPointName
        colpointAddressLine1
        collectionPointId
        collectorCompanyName
        wastereceptionpointCompanyName
        dateStorage
        vehPlateNumber
        driverFullName
        netWeight
        receptionNetWeight
        finalDestinationDate
        finalDestinationNetWeight
        finaldestinationCompanyName
        finaldestinationCompanyNoteDocNumber
      }
    }
  }
`;

export const gqlAdd = gql`
  mutation addInternalTransaction($data: InternalTransactionInput) {
    addInternalTransaction(data: $data) {
      id
    }
  }
`;
export const gqlUpdate = gql`
  mutation updateInternalTransaction($data: InternalTransactionInput) {
    updateInternalTransaction(data: $data) {
      id
    }
  }
`;

export const gqlDelete = gql`
  mutation deleteInternalTransaction($id: Int!) {
    deleteInternalTransaction(id: $id)
  }
`;

export async function getWasteReceptionPointById(
  client: ApolloClient<object>,
  id: number,
): Promise<any> {
  const gqlScript = gql`
    query getWastereceptionpointById($id: Int) {
      getWastereceptionpointById(id: $id) {
        id
        name
        phone
        email
        companyVat
        companyTaxOffice
        addressLine1
        addressLine2
        addressCity
        addressArea
        postalCode
        regionId
        receptionPersonName
      }
    }
  `;
  const ret = await client.query({
    query: gqlScript,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  return ret?.data?.getWastereceptionpointById;
}

export async function getDriverByUserId(client: ApolloClient<object>, id: number): Promise<any> {
  const gqlScript = gql`
    query getQuery($where: String) {
      getDrivers(where: $where) {
        data {
          id
          name
          userId
          adrNumber
          user {
            id
            fullName
            signatureFileId
          }
        }
      }
    }
  `;
  const where = {
    userId: id,
  };
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'network-only',
  });

  return ret?.data?.getDrivers?.data[0];
}

export async function getDriverById(client: ApolloClient<object>, id: number): Promise<any> {
  const gqlScript = gql`
    query getDriverById($id: Int) {
      getDriverById(id: $id) {
        id
        name
        userId
        adrNumber
        user {
          id
          fullName
          signatureFileId
        }
      }
    }
  `;
  return new Promise((resolve, reject) => {
    client
      .query<any, { id: number }>({
        query: gqlScript,
        variables: { id },
      })
      .then((res) => {
        if (res.data[Object.keys(res.data)[0]] !== null) {
          resolve(res.data[Object.keys(res.data)[0]]);
        } else {
          reject(res.errors[0].message);
        }
      })
      .catch((err) => reject(err));
  });
}

export async function getVehicleById(client: ApolloClient<object>, id: number): Promise<any> {
  const gqlScript = gql`
    query getVehicleById($id: Int) {
      getVehicleById(id: $id) {
        id
        plateNumber
      }
    }
  `;
  return new Promise((resolve, reject) => {
    client
      .query<any, { id: number }>({
        query: gqlScript,
        variables: { id },
      })
      .then((res) => {
        if (res.data[Object.keys(res.data)[0]] !== null) {
          resolve(res.data[Object.keys(res.data)[0]]);
        } else {
          reject(res.errors[0].message);
        }
      })
      .catch((err) => reject(err));
  });
}

export async function getIdocwastetypeById(client: ApolloClient<object>, id: number): Promise<any> {
  const gqlScript = gql`
    query getQuery($id: Int) {
      getIdocwastetypeById(id: $id) {
        id
        wasteTypeName
        defEkaCode
        ekaCodeValues
        defUnClass
        unClassValues
      }
    }
  `;

  const ret = await client.query({
    query: gqlScript,
    variables: { id },
    fetchPolicy: 'network-only',
  });

  return ret?.data?.getIdocwastetypeById;
}

export function getCollectionpointById(client: ApolloClient<object>, id: number): Promise<any> {
  return new Promise((resolve, reject) => {
    client
      .query<any, { id: number }>({
        query: gql`
          query getCollectionpointById($id: Int) {
            getCollectionpointById(id: $id) {
              id
              name
              phone
              email
              groupId
              subgroupId
              subsubgroupId
              companyVat
              companyTaxOffice
              addressLine1
              addressLine2
              addressCity
              addressArea
              postalCode
              regionId
              contactPerson
            }
          }
        `,
        variables: { id },
      })
      .then((res) => {
        if (res.data.getCollectionpointById !== null) {
          resolve(res.data.getCollectionpointById);
        } else {
          reject(res.errors[0].message);
        }
      })
      .catch((err) => reject(err));
  });
}

export async function getWasteReceptionPoint(
  client: ApolloClient<object>,
  where: any,
): Promise<any> {
  const gqlScript = gql`
    query getWastereceptionpoints($where: String) {
      getWastereceptionpoints(where: $where) {
        data {
          id
          name
          isActive
          addressLine1
          phone
          companyVat
          companyTaxOffice
          receptionPersonName
        }
        recordCount
      }
    }
  `;
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'network-only',
  });

  return ret?.data?.getWastereceptionpoints;
}

export async function getContainertypes(client: ApolloClient<object>, where: any): Promise<any> {
  const gqlScript = gql`
    query getContainertypes($limit: Int, $offset: Int, $order: String, $where: String) {
      getContainertypes(limit: $limit, offset: $offset, order: $order, where: $where) {
        data {
          id
          name
        }
        recordCount
      }
    }
  `;
  const ret = await client.query({
    query: gqlScript,
    variables: { where: JSON.stringify(where) },
    fetchPolicy: 'network-only',
  });

  return ret?.data?.getContainertypes;
}

export async function getCollectorById(client: ApolloClient<object>, id: number): Promise<any> {
  const gqlScript = gql`
    query getCollectorById($id: Int) {
      getCollectorById(id: $id) {
        id
        name
        companyVat
        companyTaxOffice
        imaCode
        contractNo
        contractSignDate
        contractExpDate
        formalName
        addressLine1
        addressLine2
        addressArea
        addressCity
        postalCode
        phone
        mobile
        fax
        email
        email2
        contactperson
        notes
      }
    }
  `;

  return new Promise((resolve, reject) => {
    client
      .query<any, { id: number }>({
        query: gqlScript,
        variables: { id },
      })
      .then((res) => {
        if (res.data[Object.keys(res.data)[0]] !== null) {
          resolve(res.data[Object.keys(res.data)[0]]);
        } else {
          reject(res.errors[0].message);
        }
      })
      .catch((err) => reject(err));
  });
}
